import { gql } from '@apollo/client'
import type { GetServerSideProps, NextPage } from 'next'
import { useTranslation } from 'next-i18next'
import { serverSideTranslations } from 'next-i18next/serverSideTranslations'
import { BreadcrumbJsonLd, NextSeo } from 'next-seo'
import dynamic from 'next/dynamic'
import React from 'react'
import getApolloClient from 'src/apollo-client'
import DirectoryListings from 'src/components/DirectoryListings'
import Footer from 'src/components/Footer'
import LandingNav from 'src/components/LandingNav'
import { CARD_LOCATION_FIELDS } from 'src/constants/fragments'
import { ILocation, IQuery, IQuerySearchLocationsArgs } from 'src/types/graphql'
import { RedirectUrls } from 'src/types/types'
import { processRequestError } from 'src/utils/errors'
const WhatsAppButton = dynamic(() => import('src/components/WhatsappButton'))

export const DIR_PAGE_SIZE = 10
const SEARCH_LOCATIONS = gql`
  ${CARD_LOCATION_FIELDS}
  query SearchLocations(
    $pagination: PaginationInput!
    $searchInput: SearchLocationsInput!
  ) {
    searchLocations(pagination: $pagination, searchInput: $searchInput) {
      total
      hits {
        ...CardLocationFields
      }
    }
  }
`

const { title, description, image, url } = {
  title: (page: number) => `SpotMe | Directorio de Espacios - Página ${page}`,
  description:
    'Mini-bodegas, estacionamientos, bodegas y naves industriales en renta en las principales ciudades de México. Explora el directorio completo de SpotMe.',
  image: 'https://www.spotme.mx/images/cover.png',
  url: (page: number) =>
    `https://www.spotme.mx${RedirectUrls.Directory}/listings/${page}`
}

type IProps = {
  locations: ILocation[]
  page: number
  pageCount: number
}

const Page: NextPage<IProps> = ({ locations, page, pageCount }) => {
  const { t } = useTranslation('common')
  const breadcrumbs = [
    {
      title: `Alojamientos #${page}`,
      url: `${RedirectUrls.Directory}/listings/${page}`
    }
  ]

  return (
    <div>
      <NextSeo
        title={title(page)}
        description={description}
        canonical={url(page)}
        openGraph={{
          type: 'website',
          url: url(page),
          title: title(page),
          description,
          images: [{ url: image, width: 1200, height: 630 }]
        }}
      />
      <BreadcrumbJsonLd
        itemListElements={[
          { position: 1, name: 'SpotMe', item: 'https://www.spotme.mx' },
          {
            position: 2,
            name: 'Directorio',
            item: 'https://www.spotme.mx/directory'
          },
          {
            position: 3,
            name: `Listado - Página ${page}`,
            item: `https://www.spotme.mx${RedirectUrls.Directory}/listings/${page}`
          }
        ]}
      />

      <main>
        <LandingNav />
        <DirectoryListings
          titleDir="Listado de alojamientos"
          breadcrumbs={breadcrumbs}
          locations={locations}
          page={page}
          pageCount={pageCount}
        />
        <Footer light />
        <WhatsAppButton msg={t('WHATSAPP_DIRECTORY_MSG')} />
      </main>
    </div>
  )
}

export default React.memo(Page)

export const getServerSideProps: GetServerSideProps<IProps | {}> = async (
  context
) => {
  const { locale, query } = context
  const { page: p } = query
  const page = parseInt(p as string, 10) || 1

  const client = getApolloClient(context)
  let response
  try {
    response = await client.query<
      Pick<IQuery, 'searchLocations'>,
      IQuerySearchLocationsArgs
    >({
      query: SEARCH_LOCATIONS,
      variables: {
        pagination: {
          from: (page - 1) * DIR_PAGE_SIZE,
          pageSize: DIR_PAGE_SIZE
        },
        searchInput: {
          center: { lat: 19.42847, lng: -99.12766 },
          zoom: 1
        }
      }
    })
  } catch (error) {
    processRequestError(error, 'Error retrieving locations.')
    return {
      props: {},
      redirect: {
        destination: RedirectUrls.Home,
        permanent: false
      }
    }
  }

  const translations = await serverSideTranslations(locale ?? 'es', [
    'footer',
    'nav',
    'platform_stats',
    'common',
    'auth'
  ])
  return {
    props: {
      ...translations,
      locations: response.data?.searchLocations?.hits ?? [],
      page,
      pageCount: Math.ceil(
        (response.data?.searchLocations?.total ?? 0) / DIR_PAGE_SIZE
      )
    }
  }
}
